import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CurrencyFormatterService } from '../../services/currency-formatter.service';
import { FeeChargeRowErrors, FeeChargeValidatorService } from '../../services/fee-charge-validator.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { Charge } from '../../models/configurations';
import { FeeEventOption, FeeChargeRow, SubValueOption } from '../../models/fee-config';

export type { FeeChargeRow };
export { FeeChargeRowErrors };

@Component({
  selector: 'app-fee-charge-config',
  templateUrl: './fee-charge-config.component.html',
  styleUrls: ['./fee-charge-config.component.css'],
})
export class FeeChargeConfigComponent implements OnChanges {
  @Input() charges: Charge[] = [];
  @Input() isDisabled: boolean = false;
  @Input() rows: FeeChargeRow[] = [];
  @Input() oldRows: FeeChargeRow[] = [];
  @Input() feeEventTypes: FeeEventOption[] = [];
  @Input() figmaLayout: boolean = false;
  @Input() nameHeader: string = $localize`:@@fee_charge:Fee/Charge`;

  @Output() rowsChange = new EventEmitter<FeeChargeRow[]>();
  @Output() rowDeleted = new EventEmitter<number>();

  rowErrors: FeeChargeRowErrors[] = [];
  showErrors = false;

  private _uidCounter = 0;

  constructor(
    private currencyFormatter: CurrencyFormatterService,
    private validator: FeeChargeValidatorService,
    private toast: NotificationService,
  ) {
    this.cachedEventTypes = this.feeChargeEventTypes;
  }

  cachedEventTypes: FeeEventOption[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rows'] && this.rows) {
      this.rows.forEach(row => {
        if (row._uid === undefined) row._uid = ++this._uidCounter;
        if (row.chargeType) {
          if (row.minimumValue) row.minimumValue = this.asCurrency(row.minimumValue);
          if (row.maximumValue) row.maximumValue = this.asCurrency(row.maximumValue);
        }
      });
      this.updateErrors();
    }
    if (changes['feeEventTypes']) {
      this._computeEventTypes();
    }
  }

  private _computeEventTypes(): void {
    if (!this.feeEventTypes?.length) {
      this.cachedEventTypes = this.feeChargeEventTypes;
      return;
    }
    this.cachedEventTypes = this.feeEventTypes.map(eventType => {
      const local = this.feeChargeEventTypes.find(localType => localType.value === eventType.value);
      return local ? { ...eventType, label: local.label } : eventType;
    });
  }

  // ── Validation ────────────────────────────────────────────────────────────

  updateErrors() {
    this.rowErrors = this.validator.validateAll(this.rows, this.eventTypeMap);
  }

  /** Called by parent via ViewChild before submitting */
  isValid(): boolean {
    this.showErrors = true;
    this.updateErrors();

    const errorMessages: string[] = [];
    this.rowErrors.forEach((rowError, rowIndex) => {
      const rowLabel = `Row ${rowIndex + 1}`;
      if (rowError.chargeId)         errorMessages.push(`${rowLabel}: ${rowError.chargeId}`);
      if (rowError.duplicate)        errorMessages.push(`${rowLabel}: ${rowError.duplicate}`);
      if (rowError.eventType)        errorMessages.push(`${rowLabel}: ${rowError.eventType}`);
      if (rowError.eventSubValue)    errorMessages.push(`${rowLabel}: ${rowError.eventSubValue}`);
      if (rowError.monthlyCustomDay) errorMessages.push(`${rowLabel}: ${rowError.monthlyCustomDay}`);
      if (rowError.q1Month)          errorMessages.push(`${rowLabel}: ${rowError.q1Month}`);
      if (rowError.q2Month)          errorMessages.push(`${rowLabel}: ${rowError.q2Month}`);
      if (rowError.q3Month)          errorMessages.push(`${rowLabel}: ${rowError.q3Month}`);
      if (rowError.q4Month)          errorMessages.push(`${rowLabel}: ${rowError.q4Month}`);
      if (rowError.h1Month)          errorMessages.push(`${rowLabel}: ${rowError.h1Month}`);
      if (rowError.h2Month)          errorMessages.push(`${rowLabel}: ${rowError.h2Month}`);
      if (rowError.minMax)           errorMessages.push(`${rowLabel}: ${rowError.minMax}`);
    });

    errorMessages.forEach(msg => this.toast.error(msg));
    return errorMessages.length === 0;
  }

  hasError(index: number, field: keyof FeeChargeRowErrors): boolean {
    return this.showErrors && !!this.rowErrors[index]?.[field];
  }

  // ── Currency helpers ──────────────────────────────────────────────────────

  private asCurrency(value: string): string {
    if (!value) return value;
    const formatted = this.currencyFormatter.formatCurrency(value);
    if (formatted === '-') {
      const num = this.currencyFormatter.parseCurrency(value);
      return num === 0 ? `${this.currencyFormatter.getCurrencySymbol()}0` : '';
    }
    return formatted;
  }

  // ── Event / month lists ───────────────────────────────────────────────────

  readonly feeChargeEventTypes: FeeEventOption[] = [
    { label: 'Daily',                   value: 'DAILY',                scheduleType: 'TIME' },
    { label: 'Weekly',                  value: 'WEEKLY',               scheduleType: 'DAY_OF_WEEK' },
    { label: 'Monthly',                 value: 'MONTHLY',              scheduleType: 'DAY_OF_MONTH' },
    { label: 'Quarterly',               value: 'QUARTERLY',            scheduleType: 'QUARTER_MONTHS' },
    { label: 'Semi-Annually',           value: 'SEMI_ANNUALLY',        scheduleType: 'HALF_MONTHS' },
    { label: 'Annually',                value: 'ANNUALLY',             scheduleType: 'MONTH' },
    { label: 'Interest Earned',         value: 'INTEREST_EARNED',      scheduleType: 'NONE' },
    { label: 'Overdraft Posting',       value: 'OVERDRAFT_POSTING',    scheduleType: 'NONE' },
    { label: 'Deposit',                 value: 'DEPOSIT_OWN_ACCOUNT',  scheduleType: 'NONE' },
    { label: 'Third-Party Deposit',     value: 'DEPOSIT_THIRD_PARTY',  scheduleType: 'NONE' },
    { label: 'Transfer to Third Party', value: 'TRANSFER_THIRD_PARTY', scheduleType: 'NONE' },
    { label: 'Transfer to Own Account', value: 'TRANSFER_OWN_ACCOUNT', scheduleType: 'NONE' },
    { label: 'SMS Notification',        value: 'SMS_NOTIFICATION',     scheduleType: 'NONE' },
    { label: 'Email Notification',      value: 'EMAIL_NOTIFICATION',   scheduleType: 'NONE' },
    { label: 'Withdrawal',              value: 'WITHDRAWAL',           scheduleType: 'NONE' },
  ];

  readonly weekDays = [
    { label: 'Monday',    value: 'MONDAY' },
    { label: 'Tuesday',   value: 'TUESDAY' },
    { label: 'Wednesday', value: 'WEDNESDAY' },
    { label: 'Thursday',  value: 'THURSDAY' },
    { label: 'Friday',    value: 'FRIDAY' },
    { label: 'Saturday',  value: 'SATURDAY' },
    { label: 'Sunday',    value: 'SUNDAY' },
  ];

  readonly monthlyOptions = [
    { label: 'First Day of the Month', value: 'FIRST_DAY' },
    { label: 'Last Day of the Month',  value: 'LAST_DAY' },
    { label: 'Custom',                 value: 'CUSTOM' },
  ];

  readonly months = [
    { label: 'Jan', value: 'JANUARY' },
    { label: 'Feb', value: 'FEBRUARY' },
    { label: 'Mar', value: 'MARCH' },
    { label: 'Apr', value: 'APRIL' },
    { label: 'May', value: 'MAY' },
    { label: 'Jun', value: 'JUNE' },
    { label: 'Jul', value: 'JULY' },
    { label: 'Aug', value: 'AUGUST' },
    { label: 'Sep', value: 'SEPTEMBER' },
    { label: 'Oct', value: 'OCTOBER' },
    { label: 'Nov', value: 'NOVEMBER' },
    { label: 'Dec', value: 'DECEMBER' },
  ];

  private readonly quarterlyDerivations: Record<string, [string, string, string]> = {
    'JANUARY':  ['APRIL',     'JULY',      'OCTOBER'],
    'FEBRUARY': ['MAY',       'AUGUST',    'NOVEMBER'],
    'MARCH':    ['JUNE',      'SEPTEMBER', 'DECEMBER'],
  };

  private readonly semiAnnualDerivations: Record<string, string> = {
    'JANUARY':  'JULY',
    'FEBRUARY': 'AUGUST',
    'MARCH':    'SEPTEMBER',
    'APRIL':    'OCTOBER',
    'MAY':      'NOVEMBER',
    'JUNE':     'DECEMBER',
  };

  getMonthShortLabel(monthValue: string): string {
    return this.months.find(month => month.value === monthValue)?.label ?? '';
  }

  readonly q1Months = this.months.slice(0, 3);
  readonly q2Months = this.months.slice(3, 6);
  readonly q3Months = this.months.slice(6, 9);
  readonly q4Months = this.months.slice(9, 12);
  readonly h1Months = this.months.slice(0, 6);
  readonly h2Months = this.months.slice(6, 12);

  readonly timeOptions = [
    '12:00AM','01:00AM','02:00AM','03:00AM','04:00AM','05:00AM',
    '06:00AM','07:00AM','08:00AM','09:00AM','10:00AM','11:00AM',
    '12:00PM','01:00PM','02:00PM','03:00PM','04:00PM','05:00PM',
    '06:00PM','07:00PM','08:00PM','09:00PM','10:00PM','11:00PM','11:59PM',
  ];

  readonly allSubValueOptions: SubValueOption[] = [
    ...this.weekDays,
    ...this.monthlyOptions,
    ...this.months,
    ...this.timeOptions.map(time => ({ label: time, value: time })),
  ];

  private get eventTypeMap(): Map<string, string> {
    return new Map(this.cachedEventTypes.map(eventType => [eventType.value, eventType.scheduleType ?? '']));
  }

  // ── Row operations ────────────────────────────────────────────────────────

  emitRows() {
    this.updateErrors();
    const cleanRows = this.rows.map(row => ({
      ...row,
      minimumValue: String(row.minimumValue || '').replace(/[^0-9.]/g, ''),
      maximumValue: String(row.maximumValue || '').replace(/[^0-9.]/g, ''),
    }));
    this.rowsChange.emit(cleanRows);
  }

  trackByRow(index: number, row: FeeChargeRow): number {
    return row._uid ?? index;
  }

  addRow() {
    this.rows = [...this.rows, {
      _uid: ++this._uidCounter,
      chargeId: null, chargeType: '', value: '',
      minimumValue: '', maximumValue: '',
      eventType: '', eventSubValue: '', monthlyCustomDay: '',
      q1Month: '', q2Month: '', q3Month: '', q4Month: '',
      h1Month: '', h2Month: '',
    }];
    this.emitRows();
  }

  drop(event: CdkDragDrop<FeeChargeRow[]>) {
    if (event.previousIndex === event.currentIndex) return;
    const reorderedRows = [...this.rows];
    moveItemInArray(reorderedRows, event.previousIndex, event.currentIndex);
    this.rows = reorderedRows;
    this.emitRows();
  }

  removeRow(index: number) {
    const row = this.rows[index];
    if (row?.id) this.rowDeleted.emit(row.id);
    this.rows = this.rows.filter((_, rowIndex) => rowIndex !== index);
    this.emitRows();
  }

  onChargeSelect(row: FeeChargeRow, chargeId: number) {
    const charge = this.charges?.find(foundCharge => foundCharge.id === chargeId);
    if (charge) {
      row.chargeType = charge.chargeType;
      row.value = charge.amount != null ? String(charge.amount) : '';
      if (charge.chargeType === 'FLAT') {
        const formatted = this.asCurrency(row.value);
        row.minimumValue = formatted;
        row.maximumValue = formatted;
      } else {
        row.minimumValue = '';
        row.maximumValue = '';
      }
    }
    this.emitRows();
  }

  onEventTypeChange(row: FeeChargeRow) {
    row.eventSubValue = '';
    row.monthlyCustomDay = '';
    row.q1Month = ''; row.q2Month = ''; row.q3Month = ''; row.q4Month = '';
    row.h1Month = ''; row.h2Month = '';
    this.emitRows();
  }

  /** Returns true when the event type renders a sub-value control after it */
  eventHasSubValue(eventType: string): boolean {
    const scheduleType = this.cachedEventTypes.find(et => et.value === eventType)?.scheduleType;
    return !!scheduleType && scheduleType !== 'NONE';
  }

  onH1MonthChange(row: FeeChargeRow) {
    row.h2Month = this.semiAnnualDerivations[row.h1Month] ?? '';
    this.emitRows();
  }

  onQ1MonthChange(row: FeeChargeRow) {
    const derived = this.quarterlyDerivations[row.q1Month] ?? ['', '', ''];
    row.q2Month = derived[0];
    row.q3Month = derived[1];
    row.q4Month = derived[2];
    this.emitRows();
  }
}
